/**
 * Team/TeamAdmin.jsx
 * ════════════════════════════════════════════════════════════════
 * Адмін-інтерфейс розділу "Команда" садочка.
 *
 * Props:
 *   kgId         {string}
 *   kindergartenId {string}  — аліас для kgId
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faUser, faGripVertical,
    faGlobe, faEyeSlash, faCheck, faSave,
    faImage, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { constr_uri } from '../../../../../services/apiClient';
import apiClient from '../../../../../services/apiClient';
import RichEditor from '../../../../../components/RichEditor/Richeditor';

// ── API ───────────────────────────────────────────────────────────────────────

var teamAPI = {
    getSettings: async function(kgId) {
        var r = await apiClient.get('team-settings/' + kgId);
        return r.data || {};
    },
    saveSettings: async function(kgId, data) {
        var r = await apiClient.put('team-settings/' + kgId, data);
        return r.data || {};
    },
    getMembers: async function(kgId, search) {
        var url = 'team-members/' + kgId;
        if (search) url += '?search=' + encodeURIComponent(search);
        var r = await apiClient.get(url);
        return r.data || [];
    },
    togglePublic: async function(kgId, empId, isPublic) {
        var r = await apiClient.put('employees/' + kgId + '/' + empId + '/public-profile', { isPublic: isPublic });
        return r.data;
    },
    saveOrder: async function(kgId, order) {
        var r = await apiClient.put('employees/' + kgId + '/display-order', { order: order });
        return r.data;
    },
};

// ── Утиліти ───────────────────────────────────────────────────────────────────

var GROUP_LABELS = { 1: 'Керівництво', 2: 'Вихователі та педагоги', 3: 'Інші' };
var GROUP_COLORS = { 1: '#0c447c', 2: '#085041', 3: '#444441' };
var GROUP_BG     = { 1: '#e6f1fb', 2: '#e1f5ee', 3: '#f1efe8' };

function groupMembers(members) {
    var groups = { 1: [], 2: [], 3: [] };
    members.forEach(function(m) {
        var g = m.displayGroup || 3;
        if (!groups[g]) groups[g] = [];
        groups[g].push(m);
    });
    return groups;
}

// ── Картка співробітника ───────────────────────────────────────────────────────

var MemberCard = function(props) {
    var m        = props.member;
    var kgId     = props.kgId;
    var onToggle = props.onToggle;
    var dragHandleProps = props.dragHandleProps || {};
    var isDragging = props.isDragging || false;

    var avatarSrc = m.hasPhoto && m.photoDocId && m.avatarFileId
        ? constr_uri('docfile?doc_id=' + m.photoDocId + '&file_id=' + m.avatarFileId)
        : null;

    var [toggling, setToggling] = useState(false);

    var handleToggle = async function() {
        setToggling(true);
        try {
            await teamAPI.togglePublic(kgId, m.id, !m.isPublic);
            onToggle(m.id, !m.isPublic);
        } catch(e) {
            toast.error('Помилка зміни публічності');
        } finally {
            setToggling(false);
        }
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px',
            border: '1px solid ' + (isDragging ? '#86b7fe' : '#e5e7eb'),
            borderRadius: 8,
            background: isDragging ? '#f0f7ff' : '#fff',
            boxShadow: isDragging ? '0 4px 16px rgba(13,110,253,0.15)' : '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.15s, border-color 0.15s',
            cursor: 'default',
            userSelect: 'none',
        }}>
            {/* Drag handle */}
            <div
                {...dragHandleProps}
                style={{
                    color: '#d1d5db', cursor: 'grab', flexShrink: 0,
                    padding: '2px 4px', fontSize: 14,
                }}
                title="Перетягнути"
            >
                <FontAwesomeIcon icon={faGripVertical} />
            </div>

            {/* Аватар */}
            <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: '#f3f4f6', flexShrink: 0, overflow: 'hidden',
                border: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
            }}>
                {avatarSrc ? (
                    <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <FontAwesomeIcon icon={faUser} style={{ fontSize: 16, color: '#d1d5db' }} />
                )}
                {/* Індикатор відсутності фото */}
                {!m.hasPhoto && (
                    <div style={{
                        position: 'absolute', bottom: -2, right: -2,
                        width: 14, height: 14, borderRadius: '50%',
                        background: '#fbbf24', border: '2px solid #fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} title="Фото відсутнє">
                        <FontAwesomeIcon icon={faImage} style={{ fontSize: 7, color: '#fff' }} />
                    </div>
                )}
            </div>

            {/* Інфо */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.fullName}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.position || '—'}
                    {m.age ? <span style={{ marginLeft: 6, color: '#adb5bd' }}>{m.age} р.</span> : null}
                </div>
            </div>

            {/* Toggle публічності */}
            <button
                type="button"
                onClick={handleToggle}
                disabled={toggling}
                title={m.isPublic ? 'Приховати з публічної сторінки' : 'Показати на публічній сторінці'}
                style={{
                    width: 44, height: 24, borderRadius: 12, border: 'none',
                    background: m.isPublic ? '#059669' : '#d1d5db',
                    position: 'relative', cursor: toggling ? 'wait' : 'pointer',
                    transition: 'background 0.2s', flexShrink: 0,
                    opacity: toggling ? 0.6 : 1,
                }}
            >
                <span style={{
                    position: 'absolute', top: 3,
                    left: m.isPublic ? 23 : 3,
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#fff', transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
            </button>
        </div>
    );
};

// ── Drag-and-drop (нативний HTML5) ────────────────────────────────────────────

var DraggableList = function(props) {
    var members    = props.members;
    var kgId       = props.kgId;
    var groupKey   = props.groupKey;
    var onToggle   = props.onToggle;
    var onReorder  = props.onReorder;
    var onMoveTo   = props.onMoveTo; // (id, fromGroup, toGroup)

    var dragItem   = useRef(null);
    var dragGroup  = useRef(null);
    var [dragOver, setDragOver] = useState(null);

    var handleDragStart = function(e, id, group) {
        dragItem.current  = id;
        dragGroup.current = group;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id + '|' + group);
    };

    var handleDragOver = function(e, id) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(id);
    };

    var handleDrop = function(e, targetId, targetGroup) {
        e.preventDefault();
        var srcId    = dragItem.current;
        var srcGroup = parseInt(dragGroup.current);
        setDragOver(null);
        dragItem.current  = null;
        dragGroup.current = null;

        if (srcId === targetId) return;

        if (srcGroup !== targetGroup) {
            // Переміщення між групами
            onMoveTo(srcId, srcGroup, targetGroup, targetId);
        } else {
            // Зміна порядку всередині групи
            onReorder(srcId, targetId, targetGroup);
        }
    };

    var handleDragEnd = function() {
        setDragOver(null);
        dragItem.current  = null;
        dragGroup.current = null;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            onDragOver={function(e) { e.preventDefault(); }}
            onDrop={function(e) {
                // Drop на порожню групу
                var data = e.dataTransfer.getData('text/plain');
                if (!data) return;
                var parts = data.split('|');
                var srcId    = parts[0];
                var srcGroup = parseInt(parts[1]);
                if (srcGroup !== parseInt(groupKey) && members.length === 0) {
                    onMoveTo(srcId, srcGroup, parseInt(groupKey), null);
                }
            }}
        >
            {members.length === 0 && (
                <div style={{
                    padding: '12px', textAlign: 'center',
                    fontSize: 12, color: '#adb5bd',
                    border: '1.5px dashed #e5e7eb', borderRadius: 8,
                    minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    Перетягніть сюди
                </div>
            )}
            {members.map(function(m) {
                return (
                    <div
                        key={m.id}
                        draggable
                        onDragStart={function(e) { handleDragStart(e, m.id, m.displayGroup); }}
                        onDragOver={function(e) { handleDragOver(e, m.id); }}
                        onDrop={function(e) { handleDrop(e, m.id, parseInt(groupKey)); }}
                        onDragEnd={handleDragEnd}
                        style={{ opacity: dragItem.current === m.id ? 0.4 : 1, transition: 'opacity 0.15s' }}
                    >
                        <div style={{
                            borderRadius: 8,
                            border: dragOver === m.id ? '2px dashed #86b7fe' : '2px solid transparent',
                            transition: 'border-color 0.1s',
                        }}>
                            <MemberCard
                                member={m}
                                kgId={kgId}
                                onToggle={onToggle}
                                dragHandleProps={{
                                    onMouseDown: function(e) { e.currentTarget.closest('[draggable]').setAttribute('draggable', true); },
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Секція групи ──────────────────────────────────────────────────────────────

var GroupSection = function(props) {
    var groupKey  = props.groupKey;
    var members   = props.members;
    var kgId      = props.kgId;
    var onToggle  = props.onToggle;
    var onReorder = props.onReorder;
    var onMoveTo  = props.onMoveTo;

    var [collapsed, setCollapsed] = useState(false);
    var label    = GROUP_LABELS[groupKey] || 'Інші';
    var color    = GROUP_COLORS[groupKey] || '#444';
    var bg       = GROUP_BG[groupKey]     || '#f1efe8';
    var pubCount = members.filter(function(m) { return m.isPublic; }).length;

    return (
        <div style={{ marginBottom: 16 }}>
            {/* Заголовок групи */}
            <div
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8,
                    background: bg, marginBottom: 8,
                    cursor: 'pointer',
                }}
                onClick={function() { setCollapsed(function(c) { return !c; }); }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: color }}>{label}</span>
                    <span style={{
                        fontSize: 11, padding: '1px 7px', borderRadius: 10,
                        background: 'rgba(0,0,0,0.08)', color: color, fontWeight: 600,
                    }}>
                        {members.length}
                    </span>
                    {pubCount > 0 && (
                        <span style={{
                            fontSize: 11, padding: '1px 7px', borderRadius: 10,
                            background: '#059669', color: '#fff', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                            <FontAwesomeIcon icon={faGlobe} style={{ fontSize: 9 }} />
                            {pubCount} публічних
                        </span>
                    )}
                </div>
                <FontAwesomeIcon
                    icon={collapsed ? faChevronDown : faChevronUp}
                    style={{ fontSize: 12, color: color }}
                />
            </div>

            {/* Список */}
            {!collapsed && (
                <DraggableList
                    members={members}
                    kgId={kgId}
                    groupKey={groupKey}
                    onToggle={onToggle}
                    onReorder={onReorder}
                    onMoveTo={onMoveTo}
                />
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var TeamAdmin = function(props) {
    var kgId = props.kgId || props.kindergartenId;

    var [members, setMembers]         = useState([]);
    var [description, setDescription] = useState('');
    var [settingsDocId, setSettingsDocId] = useState(null);
    var [search, setSearch]           = useState('');
    var [loading, setLoading]         = useState(true);
    var [saving, setSaving]           = useState(false);
    var [savingOrder, setSavingOrder] = useState(false);
    var [descExpanded, setDescExpanded] = useState(false);

    var searchTimeout = useRef(null);

    // ── Завантаження ──────────────────────────────────────────────────────────

    useEffect(function() {
        if (!kgId) return;
        loadAll();
    }, [kgId]);

    var loadAll = async function() {
        setLoading(true);
        try {
            var [settings, membersData] = await Promise.all([
                teamAPI.getSettings(kgId),
                teamAPI.getMembers(kgId),
            ]);
            setDescription(settings.description || '');
            setSettingsDocId(settings.id || null);
            setMembers(membersData);
        } catch(e) {
            toast.error('Помилка завантаження');
        } finally {
            setLoading(false);
        }
    };

    // ── Пошук ─────────────────────────────────────────────────────────────────

    var handleSearch = function(e) {
        var val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async function() {
            try {
                var data = await teamAPI.getMembers(kgId, val);
                setMembers(data);
            } catch(e) {}
        }, 350);
    };

    // ── Toggle публічності ────────────────────────────────────────────────────

    var handleToggle = function(id, newIsPublic) {
        setMembers(function(prev) {
            return prev.map(function(m) {
                return m.id === id ? Object.assign({}, m, { isPublic: newIsPublic }) : m;
            });
        });
    };

    // ── Зміна порядку всередині групи ─────────────────────────────────────────

    var handleReorder = function(srcId, targetId, group) {
        setMembers(function(prev) {
            var groupMembers = prev.filter(function(m) { return (m.displayGroup || 3) === group; });
            var others       = prev.filter(function(m) { return (m.displayGroup || 3) !== group; });

            var srcIdx    = groupMembers.findIndex(function(m) { return m.id === srcId; });
            var targetIdx = groupMembers.findIndex(function(m) { return m.id === targetId; });
            if (srcIdx === -1 || targetIdx === -1) return prev;

            var reordered = groupMembers.slice();
            var removed   = reordered.splice(srcIdx, 1)[0];
            reordered.splice(targetIdx, 0, removed);

            // Оновлюємо displayOrder
            reordered = reordered.map(function(m, i) {
                return Object.assign({}, m, { displayOrder: i + 1 });
            });

            return others.concat(reordered).sort(function(a, b) {
                if (a.displayGroup !== b.displayGroup) return a.displayGroup - b.displayGroup;
                return a.displayOrder - b.displayOrder;
            });
        });
    };

    // ── Переміщення між групами ───────────────────────────────────────────────

    var handleMoveTo = function(srcId, fromGroup, toGroup, beforeId) {
        setMembers(function(prev) {
            var updated = prev.map(function(m) {
                if (m.id !== srcId) return m;
                return Object.assign({}, m, { displayGroup: toGroup, displayOrder: 999 });
            });

            // Перераховуємо порядок в обох групах
            [fromGroup, toGroup].forEach(function(g) {
                var gMembers = updated.filter(function(m) { return (m.displayGroup || 3) === g; });
                var sorted   = gMembers.slice().sort(function(a, b) { return a.displayOrder - b.displayOrder; });
                sorted.forEach(function(m, i) { m.displayOrder = i + 1; });
            });

            return updated.sort(function(a, b) {
                if (a.displayGroup !== b.displayGroup) return a.displayGroup - b.displayGroup;
                return a.displayOrder - b.displayOrder;
            });
        });
    };

    // ── Зберегти порядок ──────────────────────────────────────────────────────

    var handleSaveOrder = async function() {
        setSavingOrder(true);
        try {
            var order = members.map(function(m) {
                return { id: m.id, displayOrder: m.displayOrder };
            });
            // Також зберігаємо групу через public-profile
            await Promise.all(members.map(function(m) {
                return apiClient.put(
                    'employees/' + kgId + '/' + m.id + '/public-profile',
                    { displayGroup: m.displayGroup, displayOrder: m.displayOrder }
                );
            }));
            toast.success('Порядок збережено');
        } catch(e) {
            toast.error('Помилка збереження порядку');
        } finally {
            setSavingOrder(false);
        }
    };

    // ── Зберегти опис ────────────────────────────────────────────────────────

    var handleSaveDescription = async function() {
        setSaving(true);
        try {
            await teamAPI.saveSettings(kgId, { description: description });
            toast.success('Опис збережено');
        } catch(e) {
            toast.error('Помилка збереження опису');
        } finally {
            setSaving(false);
        }
    };

    // ── Рендер ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    var groups = groupMembers(members);
    var totalPublic = members.filter(function(m) { return m.isPublic; }).length;
    var noPhoto     = members.filter(function(m) { return !m.hasPhoto; }).length;

    return (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 4px' }}>

            {/* ── Статистика ── */}
            <div style={{
                display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap',
            }}>
                {[
                    { label: 'Всього активних', value: members.length, color: '#6b7280' },
                    { label: 'Публічних',        value: totalPublic,   color: '#059669' },
                    { label: 'Без фото',          value: noPhoto,       color: noPhoto > 0 ? '#d97706' : '#6b7280' },
                ].map(function(s) {
                    return (
                        <div key={s.label} style={{
                            flex: 1, minWidth: 120,
                            background: '#fff', border: '1px solid #e5e7eb',
                            borderRadius: 8, padding: '10px 14px',
                            textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>{s.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* ── Вступний текст команди ── */}
            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 10, marginBottom: 20, overflow: 'hidden',
            }}>
                <div
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', cursor: 'pointer',
                        borderBottom: descExpanded ? '1px solid #e5e7eb' : 'none',
                    }}
                    onClick={function() { setDescExpanded(function(v) { return !v; }); }}
                >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                        Вступний текст про команду
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {description && (
                            <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>● заповнено</span>
                        )}
                        <FontAwesomeIcon
                            icon={descExpanded ? faChevronUp : faChevronDown}
                            style={{ fontSize: 12, color: '#9ca3af' }}
                        />
                    </div>
                </div>

                {descExpanded && (
                    <div style={{ padding: '14px 16px' }}>
                        <div style={{ marginBottom: 12 }}>
                            <RichEditor
                                value={description}
                                onChange={setDescription}
                                placeholder="Розкажіть про колектив садочка — традиції, цінності, підхід до виховання..."
                                maxLength={5000}
                                minHeight={150}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={handleSaveDescription}
                                disabled={saving}
                                style={{
                                    padding: '7px 18px', borderRadius: 8, fontSize: 13,
                                    background: '#0069ff', color: '#fff', border: 'none',
                                    cursor: saving ? 'wait' : 'pointer', fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    opacity: saving ? 0.7 : 1,
                                }}
                            >
                                <FontAwesomeIcon icon={faSave} />
                                {saving ? 'Зберігається...' : 'Зберегти опис'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Панель керування ── */}
            <div style={{
                display: 'flex', gap: 10, alignItems: 'center',
                marginBottom: 16, flexWrap: 'wrap',
            }}>
                {/* Пошук */}
                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                    <FontAwesomeIcon icon={faSearch} style={{
                        position: 'absolute', left: 10, top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 13, color: '#9ca3af', pointerEvents: 'none',
                    }} />
                    <input
                        type="text"
                        value={search}
                        onChange={handleSearch}
                        placeholder="Пошук за ПІБ..."
                        style={{
                            width: '100%', height: 36, paddingLeft: 32, paddingRight: 12,
                            border: '1px solid #ced4da', borderRadius: 8, fontSize: 13,
                            outline: 'none',
                        }}
                    />
                </div>

                {/* Легенда */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#6b7280' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} />
                        без фото
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 22, height: 12, borderRadius: 6, background: '#059669', display: 'inline-block' }} />
                        публічний
                    </span>
                </div>

                {/* Зберегти порядок */}
                <button
                    type="button"
                    onClick={handleSaveOrder}
                    disabled={savingOrder}
                    style={{
                        padding: '7px 16px', borderRadius: 8, fontSize: 13,
                        background: savingOrder ? '#e5e7eb' : '#fff',
                        border: '1px solid ' + (savingOrder ? '#e5e7eb' : '#0069ff'),
                        color: savingOrder ? '#9ca3af' : '#0069ff',
                        cursor: savingOrder ? 'wait' : 'pointer',
                        fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                    }}
                >
                    <FontAwesomeIcon icon={faCheck} />
                    {savingOrder ? 'Збереження...' : 'Зберегти порядок'}
                </button>
            </div>

            {/* ── Групи з картками ── */}
            {[1, 2, 3].map(function(g) {
                return (
                    <GroupSection
                        key={g}
                        groupKey={g}
                        members={groups[g] || []}
                        kgId={kgId}
                        onToggle={handleToggle}
                        onReorder={handleReorder}
                        onMoveTo={handleMoveTo}
                    />
                );
            })}

            {members.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#adb5bd' }}>
                    {search ? 'Нічого не знайдено' : 'Немає активних співробітників'}
                </div>
            )}
        </div>
    );
};

export default TeamAdmin;